/** 달력 — which day was good, which lost money, and what earned on it (S3).
 *
 *  The month as a heat grid, the month's totals, the open day's report
 *  (`?d=YYYY-MM-DD`: a day cell opens it, so the address shows the day and a
 *  reload or a link lands on it) and the last twelve months. The open day
 *  is the address's: ‹ › move it a day, 닫기 removes it.
 *
 *  The month shown is in the address too (`?m=YYYY-MM`, left out for this
 *  month), so the back button undoes a month move. An open day decides the
 *  month: moving the month closes the day.
 *
 *  Spend: the grid, the month totals and the monthly table all read
 *  `/api/report/stats` by day or month, which count every campaign's spend —
 *  one source, so the month tile and the month's row agree. Spend tied to no
 *  app is said under the totals. Spend, profit and the deficit dots follow
 *  the spend rule (lib/spend.ts); the reason is said once, under the totals. */

import { useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import type { StatsRow } from '@/api/client'
import { addDays, addMonths, monthEnd, monthOf, monthTitle, seoulToday } from '@/lib/dates'
import { count, percent } from '@/lib/money'
import { useStats } from '@/lib/queries'
import { useSpend } from '@/lib/useSpend'
import { DataTable, FilterBar, HeatCalendar, IconButton, Money, PageHeader, StatTile, TonePill, UnmappedSpend, type Column, type HeatDay } from '@/ui'
import { DayReport } from './DayReport'

const DATE = /^\d{4}-\d{2}-\d{2}$/
const MONTH = /^\d{4}-(0[1-9]|1[0-2])$/

/** The month the page shows: the open day's, else `?m=`, else this month.
 *  A malformed `?m=` is ignored. */
export function shownMonth(d: string | null, m: string | null, thisMonth: string): string {
  if (d) return monthOf(d)
  return m && MONTH.test(m) ? m : thisMonth
}

export default function CalendarPage() {
  const today = seoulToday()
  const thisMonth = monthOf(today)
  // The open day report and the month live in the address so they survive a
  // reload, can be linked, and the back button walks them back. A future day
  // or a malformed value opens nothing.
  const [params, setParams] = useSearchParams()
  const d = params.get('d')
  const selected = d && DATE.test(d) && d <= today ? d : null
  const ym = shownMonth(selected, params.get('m'), thisMonth)
  const spend = useSpend()
  const { shown, hint } = spend

  const showMonth = (month: string) => setParams(month === thisMonth ? {} : { m: month })
  const open = (date: string) => setParams({ d: date })
  const close = () => showMonth(ym)

  const days = useStats({ by: 'day', from: `${ym}-01`, to: monthEnd(ym) })
  const monthly = useStats({ by: 'month', from: addMonths(thisMonth, -11), to: thisMonth })

  const heat = useMemo<HeatDay[] | undefined>(
    () => days.data?.rows.filter((r): r is StatsRow & { date: string } => !!r.date).map((r) => ({ date: r.date, revenue: r.revenue, cost: r.cost })),
    [days.data],
  )
  const totals = days.data?.totals

  const meta = monthly.data?.meta
  const description = meta ? `${meta.timezone} · ${meta.currency}${meta.today_partial ? ' · 이번 달은 집계 중' : ''}` : 'Asia/Seoul · USD'

  type MonthRow = StatsRow & { month: string }
  const monthRows = useMemo(
    () => [...(monthly.data?.rows ?? [])].filter((r): r is MonthRow => !!r.month).reverse(),
    [monthly.data],
  )
  const columns: Column<MonthRow>[] = [
    {
      key: 'month',
      header: '월',
      card: 'title',
      cell: (m) => (
        <span className="inline-flex items-center gap-2 font-medium tabular-nums">
          {m.month.replace('-', '. ')}
          {m.month === thisMonth && <TonePill tone="publishing" label="집계 중" />}
        </span>
      ),
    },
    { key: 'revenue', header: '수익', align: 'right', cell: (m) => <Money value={m.revenue} krw /> },
    { key: 'cost', header: '지출', align: 'right', cell: (m) => <Money value={m.cost} hidden={!shown} hint={hint} /> },
    { key: 'profit', header: '순이익', align: 'right', card: 'meta', cell: (m) => <Money value={m.profit} hidden={!shown} hint={hint} /> },
    { key: 'impressions', header: '노출', align: 'right', card: 'hidden', cell: (m) => <span className="tabular-nums">{count(m.impressions)}</span> },
    { key: 'clicks', header: '클릭', align: 'right', card: 'hidden', cell: (m) => <span className="tabular-nums">{count(m.clicks)}</span> },
    { key: 'ecpm', header: 'eCPM', align: 'right', card: 'hidden', cell: (m) => <Money value={m.ecpm} /> },
    { key: 'match', header: '매칭률', align: 'right', card: 'hidden', cell: (m) => <span className="tabular-nums">{m.ad_requests > 0 ? percent(m.match_rate) : '—'}</span> },
  ]

  return (
    <>
      <PageHeader title="달력" description={description}>
        <FilterBar>
          <IconButton label="이전 달" icon={ChevronLeft} variant="secondary" onClick={() => showMonth(addMonths(ym, -1))} />
          <span className="min-w-[5.5rem] text-center text-section font-semibold tabular-nums" aria-live="polite">
            {monthTitle(ym)}
          </span>
          <IconButton label="다음 달" icon={ChevronRight} variant="secondary" onClick={() => showMonth(addMonths(ym, 1))} />
        </FilterBar>
      </PageHeader>

      <div className="space-y-4">
        <section aria-label="달력" className="rounded-lg border border-border bg-card p-3 sm:p-4">
          <HeatCalendar
            month={ym}
            days={heat}
            today={today}
            selected={selected}
            showDeficit={shown}
            onSelect={(date) => (date === selected ? close() : open(date))}
          />
          {days.error && <p role="alert" className="mt-3 text-table text-status-failed">달력을 불러오지 못했다: {(days.error as Error).message}</p>}
        </section>

        <div className="space-y-1.5">
          <div className="grid grid-cols-3 gap-3" aria-label={`${monthTitle(ym)} 합계`}>
            <StatTile label="월 수익" value={<Money value={totals?.revenue} krw />} />
            <StatTile label="월 지출" value={<Money value={totals?.cost} hidden={!shown} hint={hint} />} />
            <StatTile label="월 순이익" value={<Money value={totals?.profit} hidden={!shown} hint={hint} />} />
          </div>
          {/* The page says why spend is a dash here, once. */}
          {!shown && spend.ready && hint && <p className="text-meta text-muted-foreground">{hint}</p>}
          <UnmappedSpend amount={days.data?.unmapped_cost} shown={shown} />
        </div>

        {selected && <DayReport date={selected} today={today} onShift={(delta) => open(addDays(selected, delta))} onClose={close} />}

        <section aria-label="월별" className="space-y-2">
          <h2 className="text-section font-semibold">
            월별<span className="ml-2 text-meta font-normal text-muted-foreground">최근 12개월 · 행을 누르면 그 달을 본다</span>
          </h2>
          <DataTable
            label="월별 수익"
            columns={columns}
            rows={monthRows}
            rowKey={(m) => m.month}
            loading={monthly.isLoading}
            error={monthly.error}
            onRetry={() => monthly.refetch()}
            onRowClick={(m) => showMonth(m.month)}
            rowClassName={(m) => (m.month === ym ? 'bg-muted/60' : undefined)}
          />
        </section>
      </div>
    </>
  )
}
